import { SAMPLE_SCHEMA, SAMPLE_SLICE_10, SAMPLE_SLICE_20 } from "./constants";
import { createInitialState } from "./state";
import type {
  DemoState,
  DemoUi,
  HostedFileState,
  MarketplaceSnapshot,
  MarketplaceStreamRecord,
  RuntimeStatus,
} from "./types";
import { downloadBlob, nowStamp, parseJsonOrNull } from "./utils";

type InitWasmModule = () => Promise<unknown>;
type JsWasmAppConstructor = new () => DemoState["app"];
type GetWasmVersion = () => string;

export class DemoController {
  private readonly state: DemoState = createInitialState();

  constructor(
    private readonly ui: DemoUi,
    private readonly initWasmModule: InitWasmModule,
    private readonly wasmAppConstructor: JsWasmAppConstructor,
    private readonly getWasmVersion: GetWasmVersion
  ) {}

  attachEventHandlers(): void {
    this.ui.connectBtn.addEventListener("click", () => void this.connectRelay());
    this.ui.resetBtn.addEventListener("click", () => this.resetApp());
    this.ui.uploadBtn.addEventListener("click", () => void this.uploadSelectedFile());
    this.ui.downloadBtn.addEventListener("click", () => void this.downloadLatestFile());
    this.ui.fetchBtn.addEventListener("click", () => void this.fetchRemoteFile());
    this.ui.loadSampleBtn.addEventListener("click", () => this.loadSamplePreset());
    this.ui.createStreamBtn.addEventListener("click", () => this.createMarketplaceStream());
    this.ui.loadRemoteStreamBtn.addEventListener("click", () =>
      void this.loadRemoteMarketplaceStream()
    );
    this.ui.appendSliceBtn.addEventListener("click", () => this.appendMarketplaceSlice());
    this.ui.appendSecondSampleBtn.addEventListener("click", () =>
      this.appendMarketplaceSlice(10, SAMPLE_SLICE_10)
    );
    this.ui.downloadSnapshotBtn.addEventListener("click", () =>
      this.downloadMarketplaceSnapshot()
    );
    const streamModal = this.ui.streamModal;
    this.ui.streamModalClose?.addEventListener("click", () => this.ui.streamModal?.close());
    streamModal?.addEventListener("click", (event) => {
      if (event.target === streamModal) {
        streamModal.close();
      }
    });
    this.ui.fileInput.addEventListener("change", () => this.handleFileInputChange());
  }

  attachGlobalHandlers(): void {
    window.addEventListener("error", (event) => {
      const message = event.error?.stack ?? event.error?.message ?? event.message;
      this.consoleError(message || "Unhandled browser error");
      this.logEvent(`Browser error: ${message || "unhandled"}`);
    });

    window.addEventListener("unhandledrejection", (event) => {
      const reason =
        event.reason?.stack ?? event.reason?.message ?? String(event.reason);
      this.consoleError(reason || "Unhandled promise rejection");
      this.logEvent(`Unhandled rejection: ${reason || "unknown"}`);
    });
  }

  loadSamplePreset(): void {
    this.ui.datasetNamespaceInput.value = "demo/prices/btc-usd";
    this.ui.schemaFormatInput.value = "json-schema";
    this.ui.datasetSchemaInput.value = SAMPLE_SCHEMA;
    this.ui.sliceIdInput.value = "20";
    this.ui.slicePayloadInput.value = SAMPLE_SLICE_20;
    this.logEvent("Loaded BTC/USD marketplace sample");
  }

  async initWasm(): Promise<void> {
    try {
      await this.initWasmModule();
      this.state.app = new this.wasmAppConstructor();
      this.state.wasmReady = true;
      this.ui.wasmVersion.textContent = this.getWasmVersion();
      this.logEvent("WASM runtime loaded");
      this.refreshMarketplaceStreams();
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.consoleError(`WASM init failed: ${message}`);
      this.logEvent(`WASM init failed: ${message}`);
      this.ui.wasmVersion.textContent = "load failed";
      this.setConnectionState("error", "Failed to load wasm bundle");
      this.ui.connectBtn.disabled = true;
      this.ui.uploadBtn.disabled = true;
      this.ui.createStreamBtn.disabled = true;
      this.ui.loadRemoteStreamBtn.disabled = true;
      this.ui.appendSliceBtn.disabled = true;
      this.ui.appendSecondSampleBtn.disabled = true;
    }

    this.renderStatus();
  }

  private consoleLog(message: string): void {
    console.info(`[ds-wasm demo] ${message}`);
  }

  private consoleError(message: string): void {
    console.error(`[ds-wasm demo] ${message}`);
  }

  private logEvent(message: string): void {
    this.consoleLog(message);
    const entry = document.createElement("li");
    entry.className = "log-entry";
    entry.textContent = message;
    const stamp = document.createElement("span");
    stamp.textContent = nowStamp();
    entry.appendChild(stamp);
    this.ui.eventLog.prepend(entry);
  }

  private setConnectionState(stateText: string, detail = ""): void {
    this.state.connectionState = stateText;
    this.state.connectionDetail = detail;
    this.state.lastEvent = nowStamp();
    this.renderStatus();
  }

  private setHostedFileState(fileState: HostedFileState | null): void {
    this.state.hostedFile = fileState;
    this.renderStatus();
  }

  private setMarketplaceState(
    next: Partial<DemoState["marketplace"]> & {
      activeStream?: MarketplaceStreamRecord | null;
      snapshot?: MarketplaceSnapshot | null;
      streams?: MarketplaceStreamRecord[];
    }
  ): void {
    if (next.activeStream !== undefined) {
      this.state.marketplace.activeStream = next.activeStream;
    }
    if (next.snapshot !== undefined) {
      this.state.marketplace.snapshot = next.snapshot;
    }
    if (next.streams !== undefined) {
      this.state.marketplace.streams = next.streams;
    }
    this.renderStatus();
  }

  private parseWasmStatus(): RuntimeStatus | null {
    if (!this.state.app) {
      return null;
    }

    return parseJsonOrNull<RuntimeStatus>(this.state.app.statusJson());
  }

  private refreshMarketplaceStreams(): void {
    if (!this.state.app) {
      this.setMarketplaceState({ streams: [] });
      return;
    }

    const streams =
      parseJsonOrNull<MarketplaceStreamRecord[]>(
        this.state.app.listMarketplaceStreamsJson()
      ) ?? [];
    this.setMarketplaceState({ streams });
  }

  private getMarketplaceSnapshotJson(tagHex: string): string | null {
    if (!this.state.app || !tagHex) {
      return null;
    }

    try {
      return this.state.app.marketplaceSnapshotJson(tagHex);
    } catch {
      return null;
    }
  }

  private openMarketplaceSnapshotModal(snapshot: MarketplaceSnapshot): void {
    if (!this.ui.streamModal) {
      return;
    }

    this.ui.streamModalTag.textContent = snapshot.tag_hex ?? "--";
    this.ui.streamModalNamespace.textContent =
      snapshot.namespace_utf8 ?? "binary namespace";
    this.ui.streamModalSlices.textContent = String(snapshot.slice_count ?? 0);
    this.ui.streamModalJson.textContent = JSON.stringify(snapshot, null, 2);

    if (!this.ui.streamModal.open) {
      this.ui.streamModal.showModal();
    }
  }

  private showMarketplaceSnapshot(rawSnapshot: string): MarketplaceSnapshot | null {
    const snapshot = parseJsonOrNull<MarketplaceSnapshot>(rawSnapshot);
    if (!snapshot) {
      return null;
    }

    this.syncMarketplaceSnapshot(rawSnapshot);
    this.openMarketplaceSnapshotModal(snapshot);
    return snapshot;
  }

  private renderMarketplaceStreams(): void {
    this.ui.marketplaceStreamList.innerHTML = "";

    if (this.state.marketplace.streams.length === 0) {
      const empty = document.createElement("li");
      empty.className = "stream-entry";
      empty.textContent = "No browser-authored streams yet";
      this.ui.marketplaceStreamList.appendChild(empty);
      return;
    }

    for (const stream of this.state.marketplace.streams) {
      const entry = document.createElement("li");
      entry.className = "stream-entry";
      const title = document.createElement("strong");
      title.textContent = stream.namespace_utf8 ?? "binary namespace";

      const tag = document.createElement("span");
      tag.textContent = `${stream.tag_hex.slice(0, 20)}...`;

      const slices = document.createElement("span");
      slices.textContent = `${stream.slice_count} slices`;

      const button = document.createElement("button");
      button.type = "button";
      button.className = "ghost stream-entry-action";
      button.textContent = "View stream";
      button.addEventListener("click", () => {
        const rawSnapshot = this.getMarketplaceSnapshotJson(stream.tag_hex);
        if (!rawSnapshot) {
          this.setConnectionState("error", "marketplace stream not found");
          return;
        }

        this.ui.remoteStreamTagInput.value = stream.tag_hex;
        this.ui.activeStreamTagInput.value = stream.tag_hex;
        this.showMarketplaceSnapshot(rawSnapshot);
        this.setConnectionState(
          this.state.connectionState,
          `Opened marketplace stream ${stream.tag_hex}`
        );
      });

      entry.append(title, tag, slices, button);
      this.ui.marketplaceStreamList.appendChild(entry);
    }
  }

  private renderStatus(): void {
    this.ui.connectionState.textContent = this.state.connectionState;
    this.ui.connectionDetail.textContent = this.state.connectionDetail;
    this.ui.connectionTime.textContent = this.state.lastEvent ?? "--";
    if (this.ui.connectionStateFiles) {
      this.ui.connectionStateFiles.textContent = this.state.connectionState;
    }
    if (this.ui.connectionDetailFiles) {
      this.ui.connectionDetailFiles.textContent = this.state.connectionDetail;
    }
    if (this.ui.connectionTimeFiles) {
      this.ui.connectionTimeFiles.textContent = this.state.lastEvent ?? "--";
    }

    this.ui.wasmStatus.textContent = this.state.app
      ? this.state.app.statusJson()
      : "wasm not loaded";

    if (this.state.hostedFile) {
      this.ui.latestFileName.textContent = this.state.hostedFile.label;
      this.ui.latestFileTag.textContent = this.state.hostedFile.tagHex;
      this.ui.latestFileSize.textContent = `${this.state.hostedFile.byteLength.toLocaleString()} bytes`;
      this.ui.latestFileStatus.textContent = "Ready to download from wasm storage";
      this.ui.downloadBtn.disabled = false;
    } else {
      this.ui.latestFileName.textContent = "No file uploaded";
      this.ui.latestFileTag.textContent = "--";
      this.ui.latestFileSize.textContent = "--";
      this.ui.latestFileStatus.textContent = "Upload a file to enable download";
      this.ui.downloadBtn.disabled = true;
    }

    if (this.state.marketplace.activeStream) {
      this.ui.marketplaceTag.textContent = this.state.marketplace.activeStream.tag_hex;
      this.ui.marketplaceNamespace.textContent =
        this.state.marketplace.activeStream.namespace_utf8 ?? "binary namespace";
      this.ui.marketplaceSlices.textContent = String(
        this.state.marketplace.activeStream.slice_count
      );
      this.ui.marketplaceOwner.textContent =
        this.state.marketplace.activeStream.owner_public_key_hex;
      this.ui.downloadSnapshotBtn.disabled = !this.state.marketplace.snapshot;
    } else {
      this.ui.marketplaceTag.textContent = "No stream yet";
      this.ui.marketplaceNamespace.textContent = "--";
      this.ui.marketplaceSlices.textContent = "0";
      this.ui.marketplaceOwner.textContent = "--";
      this.ui.downloadSnapshotBtn.disabled = true;
    }

    this.ui.marketplaceSnapshot.textContent = this.state.marketplace.snapshot
      ? JSON.stringify(this.state.marketplace.snapshot, null, 2)
      : "No marketplace snapshot yet";

    this.renderMarketplaceStreams();

    const runtimeStatus = this.parseWasmStatus();
    if (runtimeStatus) {
      this.ui.fileInput.title = `Hosting limit: ${runtimeStatus.file_hosting_limit} bytes`;
    }
  }

  private async connectRelay(): Promise<void> {
    if (!this.state.wasmReady || !this.state.app) {
      this.logEvent("WASM not ready, cannot connect");
      this.setConnectionState("error", "WASM not ready");
      return;
    }

    const relayUrl = this.ui.relayInput.value.trim();
    if (!relayUrl) {
      this.setConnectionState("error", "WebSocket-relay URL is required");
      return;
    }

    this.state.app.setRelayEndpoints([relayUrl]);
    this.setConnectionState("connecting", `Attempting ${relayUrl}`);
    this.logEvent(`Connecting to ${relayUrl}`);

    try {
      const statusJson = await this.state.app.connectRelay(relayUrl);
      this.setConnectionState("connected", "WebSocket-relay connection established");
      this.logEvent(`WASM websocket-relay connect resolved: ${String(statusJson)}`);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.setConnectionState("failed", message);
      this.consoleError(`WASM websocket-relay connect failed: ${message}`);
      this.logEvent(`WASM websocket-relay connect failed: ${message}`);
    }
  }

  private async uploadSelectedFile(): Promise<void> {
    if (!this.state.wasmReady || !this.state.app) {
      this.setConnectionState("error", "WASM not ready");
      this.logEvent("WASM not ready, cannot upload file");
      return;
    }

    const file = this.ui.fileInput.files?.[0];
    if (!file) {
      this.setConnectionState("error", "Choose a file first");
      return;
    }

    const label = this.ui.fileLabelInput.value.trim() || file.name;
    const bytes = new Uint8Array(await file.arrayBuffer());

    try {
      const tagHex = this.state.app.hostFile(label, bytes);
      this.setHostedFileState({
        label,
        tagHex,
        byteLength: bytes.byteLength,
      });
      this.logEvent(`Uploaded ${file.name} as ${tagHex}`);
      this.setConnectionState(
        this.state.connectionState,
        `${file.name} stored in wasm as ${tagHex}`
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.setConnectionState("error", message);
      this.consoleError(`File upload failed: ${message}`);
      this.logEvent(`File upload failed: ${message}`);
    }
  }

  private async downloadLatestFile(): Promise<void> {
    if (!this.state.wasmReady || !this.state.app) {
      this.setConnectionState("error", "WASM not ready");
      this.logEvent("WASM not ready, cannot download file");
      return;
    }

    if (!this.state.hostedFile) {
      this.setConnectionState("error", "Upload a file first");
      return;
    }

    try {
      const bytes = this.state.app.downloadFile(this.state.hostedFile.tagHex);
      downloadBlob(bytes, this.state.hostedFile.label, "application/octet-stream");
      this.logEvent(`Downloaded ${this.state.hostedFile.label} from wasm storage`);
      this.setConnectionState(
        this.state.connectionState,
        `${this.state.hostedFile.label} downloaded from wasm storage`
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.setConnectionState("error", message);
      this.consoleError(`File download failed: ${message}`);
      this.logEvent(`File download failed: ${message}`);
    }
  }

  private async fetchRemoteFile(): Promise<void> {
    if (!this.state.wasmReady || !this.state.app) {
      this.setConnectionState("error", "WASM not ready");
      this.logEvent("WASM not ready, cannot fetch remote file");
      return;
    }

    const tagHex = this.ui.remoteTagInput.value.trim();
    if (!tagHex) {
      this.setConnectionState("error", "Enter a peer file tag first");
      return;
    }

    try {
      const bytes = await this.state.app.requestRemoteFile(tagHex);
      downloadBlob(
        bytes,
        `remote-${tagHex.slice(0, 12)}.bin`,
        "application/octet-stream"
      );
      this.logEvent(`Fetched remote file ${tagHex}`);
      this.setConnectionState(this.state.connectionState, `Fetched peer file ${tagHex}`);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.setConnectionState("error", message);
      this.consoleError(`Remote file fetch failed: ${message}`);
      this.logEvent(`Remote file fetch failed: ${message}`);
    }
  }

  private async loadRemoteMarketplaceStream(): Promise<void> {
    if (!this.state.wasmReady || !this.state.app) {
      this.setConnectionState("error", "WASM not ready");
      this.logEvent("WASM not ready, cannot load marketplace stream");
      return;
    }

    const tagHex = this.ui.remoteStreamTagInput.value.trim();
    if (!tagHex) {
      this.setConnectionState("error", "Enter a marketplace tag first");
      return;
    }

    try {
      const localSnapshot = this.getMarketplaceSnapshotJson(tagHex);
      if (localSnapshot) {
        this.showMarketplaceSnapshot(localSnapshot);
        this.ui.remoteStreamTagInput.value = tagHex;
        this.ui.activeStreamTagInput.value = tagHex;
        this.logEvent(`Opened local marketplace stream ${tagHex}`);
        this.setConnectionState(
          this.state.connectionState,
          `Opened marketplace stream ${tagHex}`
        );
        return;
      }

      const rawSnapshot = await this.state.app.requestRemoteMarketplaceSnapshot(tagHex);
      const snapshot = this.showMarketplaceSnapshot(String(rawSnapshot));
      const resolvedTag =
        snapshot?.tag_hex ?? this.state.marketplace.snapshot?.tag_hex ?? tagHex;
      this.ui.remoteStreamTagInput.value = resolvedTag;
      this.ui.activeStreamTagInput.value = resolvedTag;
      this.logEvent(`Loaded remote marketplace stream ${tagHex}`);
      this.setConnectionState(
        this.state.connectionState,
        `Loaded marketplace stream ${tagHex}`
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.setConnectionState("error", message);
      this.consoleError(`Remote marketplace load failed: ${message}`);
      this.logEvent(`Remote marketplace load failed: ${message}`);
    }
  }

  private syncMarketplaceSnapshot(rawSnapshot: string): void {
    const snapshot = parseJsonOrNull<MarketplaceSnapshot>(rawSnapshot);
    if (!snapshot) {
      return;
    }

    const current = this.state.marketplace.activeStream;
    const activeStream: MarketplaceStreamRecord = {
      ...current,
      tag_hex: snapshot.tag_hex,
      namespace_utf8: snapshot.namespace_utf8,
      schema_format: snapshot.schema_format,
      owner_public_key_hex: snapshot.owner_public_key_hex,
      slice_count: snapshot.slice_count,
      latest_slice_id:
        snapshot.slices.length > 0
          ? snapshot.slices[snapshot.slices.length - 1]?.slice_id ?? null
          : null,
    };

    this.setMarketplaceState({ activeStream, snapshot });
    this.refreshMarketplaceStreams();
  }

  private createMarketplaceStream(): void {
    if (!this.state.wasmReady || !this.state.app) {
      this.setConnectionState("error", "WASM not ready");
      return;
    }

    const namespace = this.ui.datasetNamespaceInput.value.trim();
    const schemaFormat = this.ui.schemaFormatInput.value.trim() || "json-schema";
    const schemaText = this.ui.datasetSchemaInput.value.trim();

    if (!namespace || !schemaText) {
      this.setConnectionState("error", "Namespace and schema are required");
      return;
    }

    try {
      const raw = this.state.app.createMarketplaceStream(
        namespace,
        schemaFormat,
        schemaText
      );
      const stream = parseJsonOrNull<MarketplaceStreamRecord>(raw);
      if (!stream) {
        throw new Error("invalid marketplace stream response");
      }

      this.ui.activeStreamTagInput.value = stream.tag_hex;
      this.setMarketplaceState({ activeStream: stream });
      this.syncMarketplaceSnapshot(this.state.app.marketplaceSnapshotJson(stream.tag_hex));
      this.logEvent(`Created marketplace stream ${stream.tag_hex}`);
      this.setConnectionState(
        this.state.connectionState,
        `Marketplace stream created for ${namespace}`
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.setConnectionState("error", message);
      this.consoleError(`Marketplace stream creation failed: ${message}`);
      this.logEvent(`Marketplace stream creation failed: ${message}`);
    }
  }

  private appendMarketplaceSlice(
    sliceIdOverride?: number,
    payloadOverride?: string
  ): void {
    if (!this.state.wasmReady || !this.state.app) {
      this.setConnectionState("error", "WASM not ready");
      return;
    }

    const tagHex = this.ui.activeStreamTagInput.value.trim();
    const sliceId = Number(sliceIdOverride ?? this.ui.sliceIdInput.value);
    const payloadText = payloadOverride ?? this.ui.slicePayloadInput.value.trim();

    if (!tagHex || !Number.isInteger(sliceId) || sliceId < 0 || !payloadText) {
      this.setConnectionState(
        "error",
        "Stream tag, non-negative slice ID, and payload are required"
      );
      return;
    }

    try {
      const raw = this.state.app.appendMarketplaceSlice(
        tagHex,
        BigInt(sliceId),
        payloadText
      );
      this.syncMarketplaceSnapshot(raw);
      this.logEvent(`Appended marketplace slice ${sliceId} to ${tagHex}`);
      this.setConnectionState(
        this.state.connectionState,
        `Marketplace slice ${sliceId} appended`
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.setConnectionState("error", message);
      this.consoleError(`Marketplace slice append failed: ${message}`);
      this.logEvent(`Marketplace slice append failed: ${message}`);
    }
  }

  private downloadMarketplaceSnapshot(): void {
    const snapshot = this.state.marketplace.snapshot;
    if (!snapshot) {
      this.setConnectionState("error", "Create a stream and append slices first");
      return;
    }

    downloadBlob(
      JSON.stringify(snapshot, null, 2),
      `marketplace-${snapshot.tag_hex.slice(0, 12)}.json`,
      "application/json"
    );
    this.logEvent(`Downloaded marketplace snapshot ${snapshot.tag_hex}`);
  }

  private resetApp(): void {
    if (this.state.app) {
      this.state.app.disconnectRelay();
    }

    this.state.connectionState = "idle";
    this.state.connectionDetail = "Not connected";
    this.state.lastEvent = null;
    this.state.hostedFile = null;
    this.state.marketplace.activeStream = null;
    this.state.marketplace.snapshot = null;
    this.state.marketplace.streams = [];
    this.ui.fileInput.value = "";
    this.ui.fileLabelInput.value = "";
    this.ui.remoteTagInput.value = "";
    this.ui.activeStreamTagInput.value = "";
    this.ui.remoteStreamTagInput.value = "";
    this.ui.eventLog.innerHTML = "";
    this.loadSamplePreset();
    this.refreshMarketplaceStreams();
    this.logEvent("Reset");
    this.renderStatus();
  }

  private handleFileInputChange(): void {
    const file = this.ui.fileInput.files?.[0];
    if (!file) {
      return;
    }

    if (!this.ui.fileLabelInput.value.trim()) {
      this.ui.fileLabelInput.value = file.name;
    }

    this.ui.remoteTagInput.value = this.state.hostedFile?.tagHex ?? "";
    this.logEvent(`Selected ${file.name} (${file.size} bytes)`);
    this.renderStatus();
  }
}
